/* eslint-disable max-statements */

import { Dimensions } from 'react-native';
import isEmpty from 'lodash/isEmpty';
import { isFactorTv, isPlatformWeb } from 'renative';

import { AbstractImageServiceClient } from '@24i/nxg-sdk-gluons';
import { isSvgUrl } from '@24i/nxg-sdk-quarks/src/components/Image/helpers';
import { getRuntimeConfig } from '@24i/nxg-sdk-smartott/src/Application/initApp';
import { isBaseCDNResource } from '@24i/nxg-core-utils/src/api';
import { isPhoneForImageSelection } from '@24i/nxg-sdk-gluons/src/utils/imageSelectionHelpers';
import { ORIENTATIONS } from '@24i/nxg-core-utils/src/constants';

/* fallback images */
import fallbackXL from '../../../assets/fallback_XL.png';
import fallbackL from '../../../assets/fallback_L.png';
import fallbackM from '../../../assets/fallback_M.png';
import fallbackS from '../../../assets/fallback_S.png';

/* facebook images */
import facebookXL from '../../../assets/facebook_XL.png';
import facebookL from '../../../assets/facebook_L.png';
import facebookM from '../../../assets/facebook_M.png';
import facebookS from '../../../assets/facebook_S.png';

/* twitter images */
import twitterXL from '../../../assets/twitter_XL.png';
import twitterL from '../../../assets/twitter_L.png';
import twitterM from '../../../assets/twitter_M.png';
import twitterS from '../../../assets/twitter_S.png';

const XL = 'XL';
const L = 'L';
const M = 'M';
const S = 'S';

const fallbackImage = {
    [XL]: fallbackXL,
    [L]: fallbackL,
    [M]: fallbackM,
    [S]: fallbackS,
};

const facebookImage = {
    [XL]: facebookXL,
    [L]: facebookL,
    [M]: facebookM,
    [S]: facebookS,
};

const twitterImage = {
    [XL]: twitterXL,
    [L]: twitterL,
    [M]: twitterM,
    [S]: twitterS,
};

const getFallbackImage = (size) =>
    isPlatformWeb ? `/images/fallback_${size}.png` : fallbackImage[size];

const getFacebookImage = (size) =>
    isPlatformWeb ? `/images/facebook_${size}.png` : facebookImage[size];

const getTwitterImage = (size) =>
    isPlatformWeb ? `/images/twitter_${size}.png` : twitterImage[size];

const fallback = {
    XL: getFallbackImage(XL),
    L: getFallbackImage(L),
    M: getFallbackImage(M),
    S: getFallbackImage(S),
};

const facebook = {
    XL: getFacebookImage(XL),
    L: getFacebookImage(L),
    M: getFacebookImage(M),
    S: getFacebookImage(S),
};

const twittter = {
    XL: getTwitterImage(XL),
    L: getTwitterImage(L),
    M: getTwitterImage(M),
    S: getTwitterImage(S),
};

const BASE_URI = 'https://imageresize.24i.com';
const DEFAULT_OUTPUT = 'jpg';
const DEFAULT_QUALITY = 80;
const DEFAULT_FIXED_SIZES = [
    3840, 2160, 1920, 1080, 1280, 720, 960, 540, 640, 320, 426, 240, 2000, 3000, 1200, 1800, 600,
    900, 300, 450, 200, 300, 64, 128, 256, 512, 1024,
];
/*
Sizes autogenerated by Backstage:
 - 3840x2160, 1920x1080, 1280x720, 960x540, 640x320, 426x240
 - 2000x3000, 1280x1920, 1200x1800, 600x900, 300x450, 200x300
 - 64, 128, 256, 512, 1024
 */

const { width: screenWidth } = Dimensions.get('window');

const getScreenSizePrefix = () => {
    if (screenWidth < 801) return 'S';
    if (screenWidth < 1201) return 'M';
    if (screenWidth < 1601) return 'L';
    return 'XL';
};

const fixSize = (original = 0, enable = false, options = []) => {
    if (!enable || options.length === 0) return original;
    if (options.length === 1) return options[0];
    return options.reduce((prev, curr) =>
        Math.abs(curr - original) < Math.abs(prev - original) ? curr : prev
    );
};

export class ImageServiceClient extends AbstractImageServiceClient {
    constructor(opts = {}) {
        super(opts);
        this.baseUri = opts?.baseUri || BASE_URI;
    }

    getImageUrl = (opts = {}, images = []) => {
        const { imagePath, height, width, output, quality } = opts;
        const features = getRuntimeConfig('features');
        const {
            useImageResizer = false,
            useFixesSizes = false,
            fixedSizes = DEFAULT_FIXED_SIZES,
            defaultQuality = DEFAULT_QUALITY,
        } = features?.images || {};

        // Image resizer converts images to png/jpg. When using svg this is not needed since they are unlimited scalable;
        if (isSvgUrl(imagePath)) return imagePath;

        if (useImageResizer) {
            const imageParams = [];
            imageParams.push(`url=${imagePath}`);
            if (width) imageParams.push(`w=${fixSize(width, useFixesSizes, fixedSizes)}`);
            if (height) imageParams.push(`h=${fixSize(height, useFixesSizes, fixedSizes)}`);
            imageParams.push(`q=${quality || defaultQuality}`);
            imageParams.push(`output=${output || DEFAULT_OUTPUT}`);
            const url = `${this.baseUri}/?${imageParams.join('&')}`;
            return url;
        }

        if (images.length > 0) {
            if (images.length > 1) {
                const orientation =
                    (width ?? 0) >= (height ?? 0) ? ORIENTATIONS.LANDSCAPE : ORIENTATIONS.PORTRAIT;
                const initRes = width * height;
                const posRes = images.map((item) => ({
                    res: item.size.width * item.size.height,
                    orientation: item.orientation,
                }));
                // filter by orientation
                const filteredRes = posRes.filter((item) => item.orientation === orientation);
                // if there are images with the same orientation use them, otherwise use all images
                const finalRes = filteredRes.length > 0 ? filteredRes : posRes;
                const closestRes = finalRes.reduce((prev, curr) =>
                    Math.abs(curr.res - initRes) < Math.abs(prev.res - initRes) ? curr : prev
                );

                return images[posRes.indexOf(closestRes)].url;
            }

            return images[0].url;
        }

        return imagePath;
    };

    // https://aferian.atlassian.net/wiki/spaces/PRJ001NXG/pages/50297419/Image+Type+Definition#Acceptance-criteria
    /**
     * Selects the most appropriate background image for detail screens based on device type and available assets.
     * Uses consistent logic with our context wrapper to determine device type.
     *
     * @param {object} asset - The asset containing image properties
     * @param {number} targetScreenWidth - Current screen width
     * @return {string} - URL of the selected background image
     */
    selectDetailBackgroundImage = (asset, targetScreenWidth) => {
        if (isEmpty(asset)) return '';
        const { background, backgroundPortrait, packshotLandscape, still } = asset;

        // In the client implementation, we consider tablets as non-phones
        const isPhone = isPhoneForImageSelection(targetScreenWidth, true);

        // AC 8
        if (!isPhone && background) return background;
        // AC 9
        if (!isPhone && !background) return still || packshotLandscape;
        // AC 10
        if (isPhone && backgroundPortrait) return backgroundPortrait;
        // AC 11
        if (isPhone && background && !backgroundPortrait) return background;
        // AC 12
        if (isPhone && !background) return still || packshotLandscape;
        return background || still;
    };

    getImageSource = (type) => {
        const sizePrefix = getScreenSizePrefix();

        switch (type) {
            case 'facebook':
                return facebook[sizePrefix];
            case 'twitter':
                return twittter[sizePrefix];
            case 'fallback':
            default:
                return fallback[sizePrefix];
        }
    };

    getImageSourceWithFallback = (source) => {
        if (source?.uri && typeof source?.uri === 'string') {
            return source.uri;
        }

        if (source && (typeof source === 'string' || typeof source === 'number')) {
            return source;
        }

        return this.getImageSource('fallback');
    };

    getBetterQualityAsset = (url) => {
        if (!isBaseCDNResource(url)) return url;

        return isFactorTv
            ? url?.replace('526x296', '2104x1184')?.replace('426x240', '1280x720')
            : url?.replace('526x296', '1052x592')?.replace('426x240', '960x540');
    };

    getSpecificQualityAsset = (url, targetWidth, targetHeight) => {
        if (!isBaseCDNResource(url)) return url;

        const match = url.match(/(\d+)x(\d+)/);

        if (match) {
            const currentWidth = parseInt(match[1], 10);
            const currentHeight = parseInt(match[2], 10);

            if (currentWidth !== targetWidth || currentHeight !== targetHeight) {
                return url.replace(/\d+x\d+/, `${targetWidth}x${targetHeight}`);
            }
        }
        return url;
    };

    // eslint-disable-next-line no-unused-vars
    getObscuredImage = (asset) => undefined;

    // Gets the image that matches the orientation mode from additional items
    getImagesByOrientation = (additionalItems, orientationMode) =>
        additionalItems?.map(({ images, ...rest }) => {
            const imageByOrientationIndex = Math.max(
                images?.findIndex((img) => img.orientation === orientationMode) ?? -1,
                0
            );
            const selectedImage = images?.[imageByOrientationIndex];
            return {
                ...rest,
                images: selectedImage ? [selectedImage] : [],
                background: selectedImage?.url,
            };
        });
}
